<template>
  <v-tooltip bottom>
    <template #activator="{ on, attrs }">
      <div v-bind="attrs" v-on="on">
        <component
          :is="component"
          v-model="selectedItem"
          v-show-fade="{ isFilter: isFilter }"
          :items="allOrganisations"
          :label="label"
          v-bind="[$attrs]"
          :loading="
            $apollo.queries.organisations.loading ||
            $apollo.queries.possibleOrganisations.loading
          "
          :search-input.sync="searchInput"
          :clearable="clearable"
          :outlined="outlined"
          :multiple="multiple"
          :menu-props="menuProperties"
          :class="[selectMixinClass, '']"
          item-value="id"
          item-text="displayInterfaceName"
          no-filter
          :data-test="dataTest"
          :cache-items="enableCacheItems"
          :rules="rules"
          :return-object="returnObject"
          append-icon="$iconSearch"
          hide-no-data
          :hide-details="isFilter"
          @change="
            [
              inputVisibility($event, 'change'),
              emitReturnableObject
                ? $emit('returned-object', getObjectById($event))
                : false
            ]
          "
          @focus="focused"
          @blur="handleBlur"
        >
          <template #append-outer><slot name="append-outer" /></template>
          <template #selection="{ item }">
            <span>{{ item.name }}</span>
          </template>
        </component>
      </div>
    </template>
    <span>{{
      $t(
        "common:filters.search.tooltip.FilterWithTyping",
        "Please start typing a name (min 3 characters)",
        "Tooltip for filters, when user should start typing the name"
      )
    }}</span>
  </v-tooltip>
</template>

<script>
import gql from "fraql";
import selectsMixin from "./selects_mixin";
import { cloneDeep } from "lodash-es";
export default {
  name: "AppSelectOrganisation",
  mixins: [selectsMixin],
  props: {
    value: {
      type: [String, Object, Array], // allowing different kind of use: String for single ID, Object for single object
      default: null
    },
    possibleQueryName: {
      // can commit different query kind instead of just organisations
      type: String,
      default: "organisations"
    },
    possibleValues: {
      // in case list of possible values is provided then use this instead of querying from the server
      type: Array,
      default: () => []
    },
    possibleValuesIds: {
      // in case list of possible values is provided then use this instead but query their data from the server
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default() {
        return this.$t(
          "common:organisations.filter.label",
          "Organisations",
          "Label of the organisations filter in different context"
        );
      }
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    emitReturnableObject: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    outlined: {
      type: Boolean,
      default: true
    },
    queryVariables: {
      type: Object,
      default: () => ({})
    },
    queryFragments: {
      type: Object,
      default: () => null
    },
    queryWithFormData: {
      type: Boolean,
      default: false
    },
    canBeOrganiserForMyEvents: {
      type: Boolean,
      default() {
        return false;
      }
    },
    dataTest: {
      type: String,
      default: "selectOrganisation"
    },
    rules: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    enableCacheItems: {
      type: Boolean,
      default: false
    },
    membershipStatuses: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      possibleOrganisations: [],
      organisations: [],
      search: "",
      menuProperties: {}
    };
  },
  fragments: {
    organisation: gql`
      fragment _ on Organisation {
        id
        name
        fullName
        hasVatObligation
        identifier
        phone
        email
        website
      }
    `,
    formData: gql`
      fragment _ on Organisation {
        addressText
        addressIndex
        addressCity
        addressCountryId
        businessRegCode
        email
        phone
      }
    `
  },
  computed: {
    component() {
      return this.possibleValues.length || this.possibleValuesIds.length
        ? "v-select"
        : "v-autocomplete";
    },
    selectedItem: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change", val);
      }
    },
    allOrganisations() {
      let orgs = [];
      if (this.selectedItem && this.returnObject) {
        orgs.push(this.selectedItem);
      }

      if (
        this.possibleValues.length &&
        this.possibleValues.some(x => typeof x === "object")
      ) {
        orgs = orgs.concat(this.possibleValues);
      }

      return cloneDeep(
        orgs.concat(this.organisations).concat(this.possibleOrganisations)
      ).map(this.modifyOrganisations);
    },
    allPossibleValuesIds() {
      let possibleIds = [];
      if (this.returnObject) {
        if (Array.isArray(this.selectedItem)) {
          possibleIds = [
            ...this.selectedItem.map(x => x.id),
            ...this.possibleValuesIds
          ];
        } else {
          possibleIds = [this.selectedItem?.id, ...this.possibleValuesIds];
        }
      } else {
        if (Array.isArray(this.selectedItem)) {
          possibleIds = [...this.selectedItem, ...this.possibleValuesIds];
        } else {
          possibleIds = [this.selectedItem, ...this.possibleValuesIds];
        }
      }

      return possibleIds.filter(Boolean);
    },
    searchInput: {
      get() {
        return this.search;
      },
      set(val) {
        if (Array.isArray(val)) {
          this.search = "";
          this.selectedItem = null;
        } else this.search = val;
      }
    }
  },
  created() {
    if (this.component == "v-select") {
      this.menuProperties.nudgeBottom = this.nudgeBottom;
    }
  },
  apollo: {
    possibleOrganisations: {
      query: function () {
        return gql`
        query possibleOrganisationsAppSelect(
          $keyword: String
          $organisationIds: [ID]
          $sortBy: String
          $sortDesc: Boolean
          $canBeOrganiserForMyEvents: Boolean
        ) {
          organisations(
            keyword: $keyword
            organisationIds: $organisationIds
            sortBy: $sortBy
            sortDesc: $sortDesc
            canBeOrganiserForMyEvents: $canBeOrganiserForMyEvents
          ) {
            id
            name
            shortName
            fullName
            identifier
            ${this.queryWithFormData ? this.$options.fragments.formData : ""}
          }
        }
      `;
      },
      variables() {
        return {
          sortBy: "name",
          sortDesc: false,
          organisationIds: this.allPossibleValuesIds
        };
      },
      skip() {
        return this.allPossibleValuesIds.length == 0;
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;

        this.possibleOrganisations = cloneDeep(data?.organisations ?? []).map(
          this.modifyOrganisations
        );
      },
      fetchPolicy: "cache-first"
    },
    organisations: {
      query: function () {
        return gql`
          query ${this.possibleQueryName}(
            $keyword: String
            $organisationIds: [ID]
            $sortBy: String
            $sortDesc: Boolean
            $canBeOrganiserForMyEvents: Boolean
            $membershipStatuses: [String]
            $hasActivePersonOrganisationFunctions: Boolean
          ) {
            ${this.possibleQueryName}(
              keyword: $keyword
              organisationIds: $organisationIds
              sortBy: $sortBy
              sortDesc: $sortDesc
              canBeOrganiserForMyEvents: $canBeOrganiserForMyEvents
              membershipStatuses: $membershipStatuses
              hasActivePersonOrganisationFunctions: $hasActivePersonOrganisationFunctions
            ) {
              id
              name
              fullName
              hasVatObligation
              phone
              email
              website
              ${this.queryFragments ? this.queryFragments : ""}
              ${this.$options.fragments.organisation}
              ${this.queryWithFormData ? this.$options.fragments.formData : ""}
            }
          }
        `;
      },
      debounce: 750,
      variables() {
        let variables = {
          sortBy: "name",
          sortDesc: false,
          keyword: this.search,
          canBeOrganiserForMyEvents: this.canBeOrganiserForMyEvents,
          membershipStatuses: this.membershipStatuses
        };

        if (this.queryVariables) {
          Object.assign(variables, this.queryVariables);
        }

        return variables;
      },
      skip() {
        if (this.$apollo.queries.possibleOrganisations.loading) return true;
        return !this.search || this.search.length < 3;
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;
        this.organisations = cloneDeep(
          data?.[this.possibleQueryName] ?? []
        ).map(this.modifyOrganisations);
      },
      fetchPolicy: "cache-first"
    }
  },
  methods: {
    handleBlur(evt) {
      //this.searchInput = "";
      this.inputVisibility(evt);
      this.$emit("blur");
    },
    getObjectById(id) {
      return this.organisations.find(x => x.id == id);
    },
    modifyOrganisations(x) {
      x.displayInterfaceName = x.name;
      if (x.identifier) x.displayInterfaceName += ` (${x.identifier})`;
      return x;
    }
  }
};
</script>
